import React, { useEffect, useMemo, useState } from 'react';
import { Button, Select, Switch, Tooltip, Typography, showToast } from '@attentive-platform/stem-ui';

import StateLozenge from './StateLozenge';
import ReopenModal from './ReopenModal';
import AdoptModal from './AdoptModal';
import {
  accountOwnerFieldBody,
  agingLabels,
  dateShort,
  dateTimeShort,
  fetchSalesTeamMembers,
  fetchScopeRecord,
  intakeStateFromStatus,
  linkCompanyToScopeRecord,
  linkStatusFromRecord,
  mapSalesTeam,
  patchCustomCard,
  patchScopeRecord,
  resendScopeInvite
} from './api';
import {
  RecordTurnaroundBlock,
  CustomCardModal,
  RemoveCustomCardModal,
  resolveRecordTradeTurnaround,
  customerDaysForCard,
  cellLabel,
  canManageCustomCard,
  actorDisplayName,
  buildCustomCardSetBody,
  buildCustomCardRemoveBody,
  todayLabel
} from './tat';
import {
  isTurnaroundExperienceOn,
  turnaroundExperienceHistoryLabel,
  turnaroundExperiencePatchBody,
  withPresetsEnabled
} from './turnaroundExperience';
import { formatLocaleDate } from '../../Utils/HelperFunctions';
import { linkChipTone, outcomeChipTone } from './constants';
import { DetailViewStyles as S } from './styles';

const PILOT_LABEL = {
  open: 'Pilot · open',
  closed: 'Pilot · closed won',
  closed_won: 'Pilot · closed won',
  closed_lost: 'Pilot · closed lost'
};

const OUTCOME_LABEL = {
  awaiting_adoption: 'Awaiting adoption',
  live: 'Scope of record · Live',
  open: 'Pilot · open',
  closed_won: 'Pilot · closed won',
  closed_lost: 'Pilot · closed lost'
};

const MetaItem = ({ label, value }) => (
  <S.MetaCol>
    <S.MetaLabel>{label}</S.MetaLabel>
    <S.MetaValue>{value}</S.MetaValue>
  </S.MetaCol>
);

const SidebarSection = ({ title, children, action }) => (
  <S.SidebarSection>
    <S.SidebarSectionHeader>
      <S.SidebarLabel>{title}</S.SidebarLabel>
      {action}
    </S.SidebarSectionHeader>
    {children}
  </S.SidebarSection>
);

const WaitingPanel = ({ email, anchorAt, agingText, agoText, expired, neverSent, stale }) => {
  const dateLabel = dateShort(anchorAt);

  let heading = 'Waiting on the customer';
  if (expired) heading = 'Invite link expired';
  else if (neverSent) heading = 'Not sent yet';

  let body;
  if (expired) {
    body = (
      <>
        The link sent to {email}
        {agoText ? ` ${agoText} ago` : ''} is no longer valid. Resend the invite, fill it in on a call, or fix the
        attribution below — the scope viewer stays empty until they submit on a live link.
      </>
    );
  } else if (neverSent) {
    body = (
      <>
        A magic link is ready for {email}, but it hasn&apos;t been emailed yet. Send it to the customer, fill it in on a
        call, or fix the attribution below — the scope viewer stays empty until they submit.
      </>
    );
  } else {
    body = (
      <>
        The link went to {email}
        {agoText ? ` ${agoText} ago` : ''} and nothing has been submitted yet. Nudge them, fill it in on a call, or fix
        the attribution below — the scope viewer stays empty until they submit.
      </>
    );
  }

  return (
    <S.WaitingCard>
      <S.WaitingTitleBlock>
        <Typography variant='subtitle1-medium'>{heading}</Typography>
        <Typography variant='body3' color='#4D4D4D' sx={{ lineHeight: 1.6, maxWidth: '70ch' }}>
          {body}
        </Typography>
      </S.WaitingTitleBlock>
      <S.WaitingStats>
        <S.WaitingStatCard>
          <Typography variant='body1' sx={{ fontWeight: 600 }}>
            {neverSent ? `Created · ${dateLabel}` : `Sent · ${dateLabel}`}
          </Typography>
        </S.WaitingStatCard>
        <S.WaitingStatCard>
          <S.MetaLabel>{expired ? 'Expired' : neverSent ? 'Aging' : 'Waiting'}</S.MetaLabel>
          <Typography variant='body1' sx={{ fontWeight: 600, color: expired || stale ? '#995C00' : '#1A1A1A' }}>
            {agingText || '—'}
          </Typography>
          {(expired || stale) && (
            <Typography variant='body5' color='#9E9E9E'>
              {expired ? 'resend to give them a new link' : 'over a week — worth a nudge'}
            </Typography>
          )}
        </S.WaitingStatCard>
      </S.WaitingStats>
    </S.WaitingCard>
  );
};

const modifierValue = m => {
  if (m.modifier_type === 'subjective') return m.text_value || '—';
  return (m.selected_options || []).join(', ') || '—';
};

const hasModifierAnswer = m => {
  if (!m) return false;
  if (m.modifier_type === 'subjective') return !!(m.text_value || '').trim();
  return (m.selected_options || []).length > 0;
};

const scopeHasSelectedElements = sc => (sc.elements || []).some(el => el.selected);

const TradeCard = ({
  trade,
  open,
  onToggle,
  compact,
  turnaround,
  custom,
  canAct,
  onSetCustom,
  onEditCustom,
  onRemoveCustom,
  iso
}) => {
  const [expandedItems, setExpandedItems] = useState({});
  useEffect(() => {
    setExpandedItems({});
  }, [compact]);

  const sections = useMemo(
    () =>
      (trade.sections || [])
        .map(sec => ({
          ...sec,
          assigned_scopes: (sec.assigned_scopes || []).filter(scopeHasSelectedElements)
        }))
        .filter(sec => (sec.assigned_scopes || []).length > 0),
    [trade.sections]
  );
  const allScopes = sections.flatMap(s => s.assigned_scopes || []);
  const tradeMods = useMemo(() => (trade.trade_modifiers || []).filter(hasModifierAnswer), [trade.trade_modifiers]);
  const Header = open ? S.TradeHeaderOpen : S.TradeHeaderClosed;

  return (
    <S.TradeCard>
      <Header onClick={onToggle}>
        <S.TradeHeaderLeft>
          <Typography variant='body5' color='#9E9E9E'>
            {open ? '▾' : '▸'}
          </Typography>
          <Typography variant='body2' sx={{ fontWeight: 600 }}>
            {trade.trade_name}
          </Typography>
          <Typography variant='body4' color='#808080' sx={{ whiteSpace: 'nowrap' }}>
            {allScopes.length} scope{allScopes.length === 1 ? '' : 's'}
          </Typography>
        </S.TradeHeaderLeft>
        <Typography variant='body5' color='#9E9E9E' sx={{ whiteSpace: 'nowrap' }}>
          {sections.length} section{sections.length === 1 ? '' : 's'}
          {tradeMods.length > 0 ? ` · ${tradeMods.length} trade answer${tradeMods.length === 1 ? '' : 's'}` : ''}
        </Typography>
      </Header>

      {open && (
        <div>
          <RecordTurnaroundBlock
            cfg={turnaround?.cfg}
            resolved={turnaround?.resolved}
            custom={custom}
            canAct={canAct && (!!trade.account_preset?.tat || !!custom)}
            onSet={onSetCustom}
            onEdit={onEditCustom}
            onRemove={onRemoveCustom}
            iso={iso}
            hasAccountPreset={!!trade.account_preset?.tat}
            presetDetached={!!turnaround?.presetDetached || (!!custom && !trade.account_preset?.tat)}
          />
          {sections.map(sec => {
            const scopes = sec.assigned_scopes || [];
            return (
              <S.TradeScopeBlock key={sec.section_id}>
                {sec.section_name && (
                  <Typography
                    variant='body5'
                    color='#9E9E9E'
                    sx={{ letterSpacing: '.04em', textTransform: 'uppercase', padding: '0 0 6px' }}>
                    {sec.section_name}
                  </Typography>
                )}
                {scopes.map(sc => {
                  const expanded = !compact || !!expandedItems[sc.id];
                  const elements = sc.elements || [];
                  const selected = elements.filter(e => e.selected);
                  const modifiers = (sc.modifiers || []).filter(hasModifierAnswer);
                  const Row = compact ? S.ScopeRowClickable : S.ScopeRowStatic;
                  return (
                    <S.ScopeRow key={sc.id}>
                      <Row
                        onClick={() => {
                          if (!compact) return;
                          setExpandedItems(p => ({ ...p, [sc.id]: !p[sc.id] }));
                        }}>
                        <S.ScopeRowText>
                          <Typography variant='body3' sx={{ fontWeight: 500 }}>
                            {sc.name}
                          </Typography>
                          <Typography variant='body4' color='#666666'>
                            {selected.length} of {elements.length} elements
                          </Typography>
                        </S.ScopeRowText>
                        {compact && <S.ScopeExpandHint>{expanded ? 'less' : 'more'}</S.ScopeExpandHint>}
                      </Row>
                      {expanded && (
                        <S.ScopeExpanded>
                          <S.Chips>
                            {selected.slice(0, 12).map(el => (
                              <S.Chip key={el.id}>{el.name}</S.Chip>
                            ))}
                          </S.Chips>
                          {modifiers.map(m => (
                            <S.KVLine key={m.id}>
                              <Typography variant='body4' color='#808080'>
                                {m.title}:
                              </Typography>
                              <Typography variant='body4' sx={{ fontWeight: 500 }}>
                                {modifierValue(m)}
                              </Typography>
                            </S.KVLine>
                          ))}
                        </S.ScopeExpanded>
                      )}
                    </S.ScopeRow>
                  );
                })}
              </S.TradeScopeBlock>
            );
          })}
          {trade.trade_note && (
            <S.TradeDetail>
              <Typography variant='body5' color='#9E9E9E' sx={{ letterSpacing: '.04em', textTransform: 'uppercase' }}>
                Trade note
              </Typography>
              <Typography variant='body4' color='#4D4D4D' sx={{ whiteSpace: 'pre-wrap' }}>
                {trade.trade_note}
              </Typography>
            </S.TradeDetail>
          )}
          {tradeMods.length > 0 && (
            <S.TradeDetail>
              <Typography variant='body5' color='#9E9E9E' sx={{ letterSpacing: '.04em', textTransform: 'uppercase' }}>
                Detailing &amp; deliverables
              </Typography>
              {tradeMods.map(m => (
                <S.KVLine key={m.id}>
                  <Typography variant='body4' color='#808080'>
                    {m.title}:
                  </Typography>
                  <Typography variant='body4' sx={{ fontWeight: 500 }}>
                    {modifierValue(m)}
                  </Typography>
                </S.KVLine>
              ))}
            </S.TradeDetail>
          )}
        </div>
      )}
    </S.TradeCard>
  );
};

const DetailView = ({ recordId, fromSection, onBack, onFillOnBehalf, onMutated }) => {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attrEditing, setAttrEditing] = useState(false);
  const [attrDraft, setAttrDraft] = useState({ ae: '', fde: '', accountOwner: '', technicalOwner: '' });
  const [attrSaving, setAttrSaving] = useState(false);
  const [team, setTeam] = useState({ ae: [], se: [], accountOwners: [], technicalOwners: [] });
  const [viewMode, setViewMode] = useState('compact');
  const [openTrades, setOpenTrades] = useState({});
  const [reloadToken, setReloadToken] = useState(0);
  const [resending, setResending] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [unexpiring, setUnexpiring] = useState(false);
  const [pilotSaving, setPilotSaving] = useState(false);
  const [emailsSaving, setEmailsSaving] = useState(false);
  const [adopting, setAdopting] = useState(false);
  const [adoptOpen, setAdoptOpen] = useState(false);
  const [localHistory, setLocalHistory] = useState([]);
  const [ccPanel, setCcPanel] = useState(null);
  const [ccRemove, setCcRemove] = useState(null);
  const [ccSaving, setCcSaving] = useState(false);
  const [tatExpSaving, setTatExpSaving] = useState(false);

  const [actorEmail] = useState(() =>
    typeof localStorage !== 'undefined' ? (localStorage.getItem('userEmail') || '').trim() : ''
  );

  useEffect(() => {
    setLocalHistory([]);
    setCcPanel(null);
    setCcRemove(null);
  }, [recordId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchScopeRecord(recordId).then(({ data, error: err }) => {
      if (cancelled) return;
      if (err) {
        const msg = typeof err === 'string' ? err : err.detail || 'Failed to load record';
        showToast(msg, 'error');
        setRecord(null);
      } else {
        setRecord(data);
        const isExisting = data?.record_type === 'existing_customer' || data?.is_pilot === false;
        setAttrDraft({
          ae: data?.account_executive?.id || '',
          fde: data?.fde?.id || '',
          // Existing: KAM or EAM as account owner. Pilot KAM field: kam only — never seed EAM
          // (that made a zero-change save PATCH kam_id=<eam id>).
          accountOwner: isExisting
            ? data?.kam?.id || data?.eam?.id || data?.enterprise_account_manager?.id || data?.account_owner?.id || ''
            : data?.kam?.id || '',
          technicalOwner: data?.technical_csm?.id || data?.technical_owner?.id || ''
        });
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [recordId, reloadToken]);

  useEffect(() => {
    let cancelled = false;
    fetchSalesTeamMembers().then(({ data, error: err }) => {
      if (cancelled || err) return;
      setTeam(mapSalesTeam(data));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const currentVersion = record?.current_version || null;
  const hasScope = !!currentVersion;
  const isExistingCustomer = record?.record_type === 'existing_customer' || record?.is_pilot === false;
  const stateLabel = intakeStateFromStatus(record?.scope_form_status);
  const kamSelectItems = useMemo(
    () => (team.accountOwners || []).filter(u => u.roleTag !== 'EAM'),
    [team.accountOwners]
  );

  const personWithRole = (person, roster, roleTag) => {
    if (!person?.id && !person?.name) return '—';
    const hit = (roster || []).find(u => String(u.value) === String(person.id));
    if (hit?.label) return hit.label;
    if (person.name && roleTag) return `${person.name} · ${roleTag}`;
    return person.name || '—';
  };
  const { trades, tradesTotal } = useMemo(() => {
    const all = record?.trades_in_scope || [];
    return {
      tradesTotal: all.length,
      trades: all.filter(t =>
        (t.sections || []).some(sec =>
          (sec.assigned_scopes || []).some(sc => (sc.elements || []).some(el => el.selected))
        )
      )
    };
  }, [record]);

  const linkInfo = record ? linkStatusFromRecord(record) : { status: 'Not sent', hover: '' };
  const linkExpired = linkInfo.status === 'Expired' && stateLabel !== 'Submitted';
  const aging = useMemo(() => (record ? agingLabels(record) : null), [record]);
  const neverSent = aging?.neverSent ?? linkInfo.status === 'Not sent';

  const meta = useMemo(() => {
    if (!record) return [];
    const base = [
      { label: 'Customer email', value: record.customer_email },
      { label: 'Created', value: dateTimeShort(record.created_at) }
    ];
    if (aging?.sinceSent) {
      base.push({ label: 'Aging', value: aging.sinceSent });
    }
    if (!hasScope) return base;
    return [...base, { label: 'Trades scoped', value: `${trades.length} of ${tradesTotal}` }];
  }, [record, hasScope, trades.length, tradesTotal, aging]);

  const attribution = record
    ? record.record_type === 'existing_customer' || record.is_pilot === false
      ? [
          {
            label: 'Company',
            value: record.linked_company?.name || record.company_name || '—'
          },
          { label: 'Creator', value: record.created_by?.name || '—' },
          {
            label: 'Account owner',
            value:
              team.accountOwners.find(
                u =>
                  String(u.value) ===
                  String(
                    record.kam?.id ||
                      record.eam?.id ||
                      record.enterprise_account_manager?.id ||
                      record.account_owner?.id
                  )
              )?.label ||
              record.kam?.name ||
              record.eam?.name ||
              record.enterprise_account_manager?.name ||
              record.account_owner?.name ||
              '—'
          },
          {
            label: 'Technical owner',
            value:
              team.technicalOwners.find(
                u => String(u.value) === String(record.technical_csm?.id || record.technical_owner?.id)
              )?.label ||
              record.technical_csm?.name ||
              record.technical_owner?.name ||
              '—'
          },
          { label: 'CCs', value: record.cc_emails?.length ? `${record.cc_emails.length} recipients` : 'none' }
        ]
      : [
          { label: 'Prospect', value: record.company_name || '—' },
          { label: 'Creator', value: record.created_by?.name || '—' },
          {
            label: 'AE',
            value: personWithRole(record.account_executive, team.ae, 'AE')
          },
          {
            label: 'FDE',
            value: personWithRole(record.fde, team.se, 'FDE')
          },
          ...(stateLabel === 'Submitted' || hasScope
            ? [
                {
                  label: 'Key Account Manager',
                  value: personWithRole(record.kam, kamSelectItems, 'KAM')
                },
                {
                  label: 'Technical CSM',
                  value: personWithRole(record.technical_csm || record.technical_owner, team.technicalOwners, 'TCSM')
                }
              ]
            : []),
          { label: 'CCs', value: record.cc_emails?.length ? `${record.cc_emails.length} recipients` : 'none' }
        ]
    : [];

  const changeLog = useMemo(() => {
    if (!record) return [];
    const entries = [...localHistory];
    if (currentVersion) {
      const fillLabel =
        currentVersion.customer_submission === false
          ? ` · filled on behalf by ${currentVersion.actor_email || currentVersion.actor_name || 'internal'}`
          : ' · customer submission (via link)';
      entries.push({
        what: `Version v${currentVersion.version}${fillLabel}`,
        who: `${currentVersion.actor_email || 'internal'} · ${formatLocaleDate(currentVersion.created_at)}`
      });
    }
    entries.push({
      what: neverSent
        ? `Link created for ${record.customer_email} (not sent yet)`
        : `Link created and sent to ${record.customer_email}`,
      who: `${record.created_by?.name || 'creator'} · ${formatLocaleDate(record.created_at)}`
    });
    return entries;
  }, [record, currentVersion, neverSent, localHistory]);

  const canActCustom = canManageCustomCard(
    actorEmail,
    team,
    record?.can_set_custom_card ?? record?.current_version?.can_set_custom_card
  );
  // C11 — same roster gate as custom card (KAM / TCSM / Ops). AE is read-only.
  const canToggleTatExperience = canManageCustomCard(
    actorEmail,
    team,
    record?.can_set_presets_enabled ?? record?.can_toggle_presets_enabled
  );
  const tatExperienceOn = isTurnaroundExperienceOn(record);
  const actorName = actorDisplayName(actorEmail, team);
  const tatIso = currentVersion?.created_at?.slice?.(0, 10);

  const openCustomPanel = (trade, mode) => {
    const { cfg, resolved, custom } = resolveRecordTradeTurnaround(trade, record);
    if (!cfg || !resolved?.card) return;
    const was = customerDaysForCard(resolved.card, cfg.ranges.length, tatIso).map(cellLabel);
    const src = mode === 'edit' && custom ? custom.values.map(cellLabel) : was;
    const rangeBounds =
      cfg.rangeBounds ||
      (trade.account_preset?.tat?.ranges || []).map(r => ({
        lower_bound: r.lower_bound,
        upper_bound: r.upper_bound
      }));
    setCcPanel({
      trade,
      mode,
      tradeName: trade.trade_name,
      resolvedName: resolved.card.name,
      resolvedCardId: resolved.card.id,
      ranges: cfg.ranges,
      rangeBounds,
      values: src,
      was,
      reason: mode === 'edit' && custom ? custom.reason : ''
    });
  };

  const customCardErrorMessage = err => {
    if (!err) return 'Update failed';
    if (typeof err === 'string') return err;
    if (err.error) return String(err.error);
    if (err.custom_card) {
      if (Array.isArray(err.custom_card)) return err.custom_card.join(' ');
      if (typeof err.custom_card === 'object') {
        return Object.values(err.custom_card).flat().map(String).join(' ');
      }
      return String(err.custom_card);
    }
    return err.detail || 'Update failed';
  };

  const onSaveCustomCard = async ({ values, reason }) => {
    if (!ccPanel || ccSaving) return;
    const tradeId = ccPanel.trade.trade_id;
    const body = buildCustomCardSetBody(tradeId, ccPanel.rangeBounds, values, reason).custom_card;
    setCcSaving(true);
    const { error: err } = await patchCustomCard(recordId, body);
    setCcSaving(false);
    if (err) {
      showToast(customCardErrorMessage(err), 'error');
      return;
    }
    setCcPanel(null);
    showToast(
      `Custom card ${ccPanel.mode === 'edit' ? 'changed' : 'set'} for ${ccPanel.tradeName}. New version minted — no email sent.`,
      'success'
    );
    setReloadToken(t => t + 1);
    onMutated && onMutated();
  };

  const onConfirmRemoveCustom = async reason => {
    if (!ccRemove || ccSaving) return;
    const { trade, returnCardName } = ccRemove;
    setCcSaving(true);
    const body = buildCustomCardRemoveBody(trade.trade_id, reason).custom_card;
    const { error: err } = await patchCustomCard(recordId, body);
    setCcSaving(false);
    if (err) {
      showToast(customCardErrorMessage(err), 'error');
      return;
    }
    setCcRemove(null);
    showToast(`Custom card removed for ${trade.trade_name}. ${returnCardName} is back in force.`, 'success');
    setReloadToken(t => t + 1);
    onMutated && onMutated();
  };

  const backLabel = fromSection === 'records' ? '← All scope records' : '← All intake links';
  const pilotClosedLost = !isExistingCustomer && record?.outcome === 'closed_lost';
  // Pilot intakes: KAM + TCSM attribution once a version exists (Submitted / beyond).
  const showPilotOwnerFields = !isExistingCustomer && (stateLabel === 'Submitted' || hasScope);
  const emailsEnabled = record?.send_email !== false && record?.send_emails !== false;
  const outcomeLabel = (() => {
    if (!isExistingCustomer) return '';
    if (record?.outcome === 'live') return OUTCOME_LABEL.live;
    if (record?.outcome && OUTCOME_LABEL[record.outcome]) return OUTCOME_LABEL[record.outcome];
    return OUTCOME_LABEL.awaiting_adoption;
  })();

  const doPatch = async (body, successMsg, failMsg) => {
    const { data, error: err } = await patchScopeRecord(recordId, body);
    if (err) {
      showToast(failMsg || (typeof err === 'string' ? err : err.detail || 'Update failed'), 'error');
      return null;
    }
    if (successMsg) showToast(successMsg, 'success');
    setReloadToken(t => t + 1);
    onMutated && onMutated(data);
    return data;
  };

  const onToggleEmails = async (_e, checked) => {
    if (emailsSaving || pilotClosedLost) return;
    const next = typeof checked === 'boolean' ? checked : !emailsEnabled;
    setEmailsSaving(true);
    // Optimistic UI
    setRecord(r => (r ? { ...r, send_email: next, send_emails: next } : r));
    const { data, error: err } = await patchScopeRecord(recordId, { send_email: next });
    setEmailsSaving(false);
    if (err) {
      setRecord(r => (r ? { ...r, send_email: !next, send_emails: !next } : r));
      showToast(typeof err === 'string' ? err : err.detail || 'Could not update customer emails setting.', 'error');
      return;
    }
    showToast(next ? 'Customer emails turned on' : 'Customer emails turned off', 'success');
    if (data) setRecord(prev => ({ ...prev, ...data, send_email: data.send_email ?? next }));
    setReloadToken(t => t + 1);
    onMutated && onMutated(data);
  };

  /** C11 — no version minted, no email; BE writes the C7 history entry. */
  const onToggleTatExperience = async (_e, checked) => {
    if (tatExpSaving || pilotClosedLost || !canToggleTatExperience) return;
    const next = typeof checked === 'boolean' ? checked : !tatExperienceOn;
    setTatExpSaving(true);
    setRecord(r => withPresetsEnabled(r, next));
    const { data, error: err } = await patchScopeRecord(recordId, turnaroundExperiencePatchBody(next));
    setTatExpSaving(false);
    if (err) {
      setRecord(r => withPresetsEnabled(r, !next));
      showToast(
        typeof err === 'string' ? err : err.detail || err.error || 'Could not update turnaround experience.',
        'error'
      );
      return;
    }
    const label = turnaroundExperienceHistoryLabel(next);
    setLocalHistory(prev => [
      {
        what: label,
        who: `${actorName} · ${todayLabel()}`
      },
      ...prev
    ]);
    showToast(label, 'success');
    // Rely on refetch for the authoritative record — avoid clobbering fields
    // the PATCH response may omit.
    setReloadToken(t => t + 1);
    onMutated && onMutated(data);
  };

  const onSaveAttribution = async () => {
    setAttrSaving(true);
    const body = {};
    if (isExistingCustomer) {
      const currentOwner =
        record.kam?.id || record.eam?.id || record.enterprise_account_manager?.id || record.account_owner?.id || null;
      const currentTcsm = record.technical_csm?.id || record.technical_owner?.id || null;
      if (!attrDraft.accountOwner) {
        setAttrSaving(false);
        showToast('Account owner is required', 'error');
        return;
      }
      if ((attrDraft.accountOwner || null) !== currentOwner) {
        Object.assign(body, accountOwnerFieldBody(team.accountOwners, attrDraft.accountOwner));
      }
      if ((attrDraft.technicalOwner || null) !== currentTcsm) {
        body.technical_csm_id = attrDraft.technicalOwner || null;
      }
    } else {
      const currentAe = record.account_executive?.id || null;
      const currentFde = record.fde?.id || null;
      const currentKam = record.kam?.id || null;
      const currentTcsm = record.technical_csm?.id || record.technical_owner?.id || null;
      if (String(attrDraft.ae || '') !== String(currentAe || '')) {
        body.account_executive_id = attrDraft.ae || null;
      }
      if (String(attrDraft.fde || '') !== String(currentFde || '')) {
        body.fde_id = attrDraft.fde || null;
      }
      if (showPilotOwnerFields) {
        const nextKam = attrDraft.accountOwner || null;
        // Pilot KAM select excludes EAMs; never promote a seeded/stale EAM id to kam_id.
        const rosterHit = (team.accountOwners || []).find(u => String(u.value) === String(nextKam || ''));
        const nextIsEam = rosterHit?.roleTag === 'EAM';
        if (!nextIsEam && String(nextKam || '') !== String(currentKam || '')) {
          body.kam_id = nextKam;
        }
        if (String(attrDraft.technicalOwner || '') !== String(currentTcsm || '')) {
          body.technical_csm_id = attrDraft.technicalOwner || null;
        }
      }
    }
    if (!Object.keys(body).length) {
      setAttrEditing(false);
      setAttrSaving(false);
      showToast('Nothing changed', 'info');
      return;
    }
    await doPatch(body, 'Attribution updated — recorded in the change history');
    setAttrEditing(false);
    setAttrSaving(false);
  };

  const onReopen = async ({ mode, reason, notifyCustomer }) => {
    if (reopening) return;
    setReopening(true);
    const { data, error: err } = await patchScopeRecord(recordId, {
      form_closed: false,
      reopen_reason: reason,
      reopen_notify_customer: !!notifyCustomer
    });
    setReopening(false);
    if (err) {
      const detail = typeof err === 'string' ? err : err.detail || err.error || 'Could not reopen the form.';
      showToast(detail, 'error');
      return;
    }
    const successMsg =
      mode === 'notify'
        ? `Reopened — customer notified at ${record?.customer_email || 'their email'}`
        : 'Reopened for internal edits. No email sent.';
    showToast(successMsg, 'success');
    setReopenOpen(false);
    setReloadToken(t => t + 1);
    onMutated && onMutated(data);
  };

  const onUnexpire = async () => {
    if (unexpiring) return;
    setUnexpiring(true);
    await doPatch({ unexpired: true }, 'Link unexpired — the customer can use it again');
    setUnexpiring(false);
  };

  const onMarkPilotLost = async () => {
    if (pilotSaving) return;
    setPilotSaving(true);
    const { data, error: err } = await patchScopeRecord(recordId, { outcome: 'closed_lost' });
    setPilotSaving(false);
    if (err) {
      const detail =
        typeof err === 'string'
          ? err
          : Array.isArray(err.outcome)
            ? err.outcome[0]
            : err.detail || 'Could not update pilot status.';
      showToast(detail, 'error');
      return;
    }
    showToast('Pilot marked as closed lost', 'success');
    setReloadToken(t => t + 1);
    onMutated && onMutated(data);
  };

  const onAdopt = async () => {
    if (adopting || stateLabel !== 'Submitted' || record?.outcome === 'live') return;
    const companyId = record?.company_id || record?.linked_company?.id;
    if (!companyId) {
      showToast('This record has no registered company to adopt against.', 'error');
      return;
    }
    const cardId = record?.card_id || recordId;
    setAdopting(true);
    const { error: linkErr } = await linkCompanyToScopeRecord({
      scopeRecordId: recordId,
      companyId,
      cardId
    });
    if (linkErr) {
      setAdopting(false);
      showToast(
        typeof linkErr === 'string' ? linkErr : linkErr.detail || linkErr.error || 'Could not link company.',
        'error'
      );
      return;
    }
    const { data, error: patchErr } = await patchScopeRecord(recordId, { outcome: 'live' });
    setAdopting(false);
    if (patchErr) {
      showToast(typeof patchErr === 'string' ? patchErr : patchErr.detail || 'Could not set outcome to Live.', 'error');
      return;
    }
    setAdoptOpen(false);
    showToast('Adopted as scope of record', 'success');
    setReloadToken(t => t + 1);
    onMutated && onMutated(data);
  };

  const onResend = async () => {
    if (!record?.id || resending) return;
    setResending(true);
    const { data, error } = await resendScopeInvite(record.id);
    setResending(false);
    if (error) {
      const detail =
        typeof error === 'string'
          ? error
          : error.error || error.detail || (neverSent ? 'Could not send the invite.' : 'Could not resend the invite.');
      showToast(detail, 'error');
      return;
    }
    let msg = data?.message
      ? `${data.message} · ${record.customer_email}`
      : `Invite resent to ${record.customer_email || 'the customer'}`;
    if (!data?.message && neverSent) {
      msg = `Invite sent to ${record.customer_email || 'the customer'}`;
    }
    showToast(msg, 'success');
    setReloadToken(t => t + 1);
    onMutated && onMutated(data);
  };

  const onCopyLink = () => {
    if (!record) return;
    const link = record.form_link || `${window.location.origin}/scope-form/${record.id}`;
    if (navigator?.clipboard?.writeText) navigator.clipboard.writeText(link).catch(() => {});
    showToast(`Magic link for ${record.customer_email} copied`, 'success');
  };

  if (loading) {
    return <S.LoadingBox>Loading record…</S.LoadingBox>;
  }
  if (!record) {
    return (
      <S.ErrorWrap>
        <S.BackLink onClick={onBack}>{backLabel}</S.BackLink>
        <S.ErrorHint>Record could not be loaded — see the toast for details.</S.ErrorHint>
      </S.ErrorWrap>
    );
  }

  const aeOnRoster = (team.ae || []).some(u => String(u.value) === String(record.account_executive?.id));
  const fdeOnRoster = (team.se || []).some(u => String(u.value) === String(record.fde?.id));
  const kamOnRoster = kamSelectItems.some(u => String(u.value) === String(record.kam?.id));
  const aeSelectItems = (() => {
    const items = [...(team.ae || [])];
    const id = record.account_executive?.id;
    if (id && !items.some(u => String(u.value) === String(id))) {
      items.unshift({
        label: `${record.account_executive.name || 'Former AE'} · AE`,
        value: id,
        name: record.account_executive.name,
        roleTag: 'AE'
      });
    }
    return items;
  })();
  const fdeSelectItems = (() => {
    const items = [...(team.se || [])];
    const id = record.fde?.id;
    if (id && !items.some(u => String(u.value) === String(id))) {
      items.unshift({
        label: `${record.fde.name || 'Former FDE'} · FDE`,
        value: id,
        name: record.fde.name,
        roleTag: 'FDE'
      });
    }
    return items;
  })();
  const kamEditItems = (() => {
    const items = [...kamSelectItems];
    const id = record.kam?.id;
    if (id && !items.some(u => String(u.value) === String(id))) {
      items.unshift({
        label: `${record.kam.name || 'Former KAM'} · KAM`,
        value: id,
        name: record.kam.name,
        roleTag: 'KAM'
      });
    }
    return items;
  })();
  const canAdopt = isExistingCustomer && stateLabel === 'Submitted' && record.outcome !== 'live';

  return (
    <S.Wrap>
      <S.BackLink onClick={onBack}>{backLabel}</S.BackLink>

      <S.Header>
        <S.HeaderLeft>
          <S.HeaderTitleRow>
            <S.H1>{record.company_name || record.customer_email}</S.H1>
            <StateLozenge state={stateLabel} />
            {linkInfo.status && (
              <S.PilotChip tone={linkChipTone(linkInfo.status)} title={linkInfo.hover || undefined}>
                Link · {linkInfo.status}
              </S.PilotChip>
            )}
            {isExistingCustomer && outcomeLabel ? (
              <S.PilotChip tone={outcomeChipTone({ isPilot: false, outcome: record.outcome })}>
                {outcomeLabel}
              </S.PilotChip>
            ) : !isExistingCustomer && record.outcome ? (
              <S.PilotChip
                tone={outcomeChipTone({
                  isPilot: true,
                  pilotStatus: record.outcome
                })}>
                {PILOT_LABEL[record.outcome] || record.outcome}
              </S.PilotChip>
            ) : null}
          </S.HeaderTitleRow>
          <S.MetaRow>
            {meta.map(m => (
              <MetaItem key={m.label} label={m.label} value={m.value} />
            ))}
          </S.MetaRow>
        </S.HeaderLeft>

        <S.HeaderActions>
          {linkExpired && (
            <Button
              variant='contained'
              color='primary'
              size='medium'
              onClick={onUnexpire}
              loading={unexpiring}
              disabled={unexpiring || pilotClosedLost}>
              Extend link
            </Button>
          )}
          {stateLabel === 'Reopened' ? null : stateLabel === 'Submitted' || hasScope ? (
            <Button
              variant='outlined'
              color='primary'
              size='medium'
              onClick={() => setReopenOpen(true)}
              disabled={reopening || pilotClosedLost}>
              Reopen
            </Button>
          ) : (
            <Tooltip
              arrow
              placement='bottom'
              variant='dark'
              title={emailsEnabled ? undefined : 'Emails are off for this record'}>
              <span>
                <Button
                  variant='outlined'
                  color='primary'
                  size='medium'
                  onClick={onResend}
                  loading={resending}
                  disabled={resending || pilotClosedLost || !emailsEnabled}>
                  {neverSent ? 'Send to customer' : 'Resend email'}
                </Button>
              </span>
            </Tooltip>
          )}
          {stateLabel !== 'Submitted' && (
            <Button
              variant='outlined'
              color='primary'
              size='medium'
              onClick={onFillOnBehalf}
              disabled={pilotClosedLost}>
              Fill on behalf
            </Button>
          )}
          {isExistingCustomer && record.outcome !== 'live' && (
            <Tooltip
              arrow
              placement='bottom'
              variant='dark'
              title={canAdopt ? undefined : 'Adopt once a version is submitted'}>
              <span>
                <Button
                  variant='contained'
                  color='primary'
                  size='medium'
                  onClick={() => setAdoptOpen(true)}
                  disabled={!canAdopt || adopting}>
                  Adopt as scope of record
                </Button>
              </span>
            </Tooltip>
          )}
          {fromSection !== 'records' && !isExistingCustomer && !pilotClosedLost && (
            <Button
              variant='text'
              color='primary'
              size='medium'
              onClick={onMarkPilotLost}
              loading={pilotSaving}
              disabled={pilotSaving}>
              Mark pilot as closed lost
            </Button>
          )}
          <Button variant='text' color='primary' size='medium' onClick={onCopyLink} disabled={pilotClosedLost}>
            Copy link
          </Button>
        </S.HeaderActions>
      </S.Header>

      <S.BodyGrid>
        <S.Sidebar>
          {hasScope && currentVersion && (
            <>
              <S.SidebarLabel>Version history</S.SidebarLabel>
              <S.VersionCardOn>
                <S.VersionTopRow>
                  <Typography variant='body3' sx={{ fontWeight: 600 }}>
                    v{currentVersion.version}
                  </Typography>
                  <S.CurrentPill>current</S.CurrentPill>
                </S.VersionTopRow>
                {currentVersion.customer_submission === false ? (
                  <S.VersionBehalfLine>
                    <S.VersionBehalfPrefix>Filled on behalf by</S.VersionBehalfPrefix>
                    {currentVersion.actor_email ? (
                      <Tooltip arrow placement='top' variant='dark' title={currentVersion.actor_email}>
                        <S.VersionEmail>{currentVersion.actor_email}</S.VersionEmail>
                      </Tooltip>
                    ) : (
                      <span>{currentVersion.actor_name || 'internal'}</span>
                    )}
                  </S.VersionBehalfLine>
                ) : (
                  <Typography variant='body4' color='#4D4D4D'>
                    Customer submission (via link)
                  </Typography>
                )}
                <S.VersionMetaRow>
                  {currentVersion.actor_email ? (
                    <Tooltip arrow placement='top' variant='dark' title={currentVersion.actor_email}>
                      <S.VersionEmail>{currentVersion.actor_email}</S.VersionEmail>
                    </Tooltip>
                  ) : (
                    <span>internal</span>
                  )}
                  <S.VersionMetaDate>· {formatLocaleDate(currentVersion.created_at)}</S.VersionMetaDate>
                </S.VersionMetaRow>
              </S.VersionCardOn>
            </>
          )}

          <SidebarSection title='Customer emails'>
            <S.EmailsToggleRow>
              <S.EmailsToggleCopy>
                <S.EmailsToggleTitle>Send emails to the customer</S.EmailsToggleTitle>
                <S.EmailsToggleHint>
                  {emailsEnabled
                    ? 'Invite, reopen notices, and submission copies go to the customer email.'
                    : 'No customer mail for this record. Internal receipts still go.'}
                </S.EmailsToggleHint>
                {!emailsEnabled && <S.EmailsToggleNote>Turning this on does not send anything now.</S.EmailsToggleNote>}
              </S.EmailsToggleCopy>
              <Switch
                size='small'
                color='primary'
                checked={emailsEnabled}
                disabled={emailsSaving || pilotClosedLost}
                onChange={onToggleEmails}
                aria-label='Send emails to the customer'
              />
            </S.EmailsToggleRow>
            <S.EmailsToggleRow
              title={canToggleTatExperience ? undefined : 'Only KAM, Technical CSM, or Ops head can change this'}>
              <S.EmailsToggleCopy>
                <S.EmailsToggleTitle>Turnaround experience at takeoff submission</S.EmailsToggleTitle>
                <S.EmailsToggleHint>
                  {tatExperienceOn
                    ? 'Submit flow includes presets and turnaround cards.'
                    : 'Submit flow uses the M2 scope panel only — no presets or turnaround chips.'}
                </S.EmailsToggleHint>
                {!canToggleTatExperience && <S.EmailsToggleNote>Read-only for your role.</S.EmailsToggleNote>}
              </S.EmailsToggleCopy>
              <Switch
                size='small'
                color='primary'
                checked={tatExperienceOn}
                disabled={tatExpSaving || pilotClosedLost || !canToggleTatExperience}
                onChange={onToggleTatExperience}
                aria-label='Turnaround experience at takeoff submission'
              />
            </S.EmailsToggleRow>
          </SidebarSection>

          <SidebarSection
            title='Attribution'
            action={
              pilotClosedLost ? null : (
                <S.SidebarEditLink
                  onClick={() => {
                    setAttrEditing(v => {
                      const next = !v;
                      if (next && record) {
                        const existing = record.record_type === 'existing_customer' || record.is_pilot === false;
                        setAttrDraft({
                          ae: record.account_executive?.id || '',
                          fde: record.fde?.id || '',
                          accountOwner: existing
                            ? record.kam?.id ||
                              record.eam?.id ||
                              record.enterprise_account_manager?.id ||
                              record.account_owner?.id ||
                              ''
                            : record.kam?.id || '',
                          technicalOwner: record.technical_csm?.id || record.technical_owner?.id || ''
                        });
                      }
                      return next;
                    });
                  }}>
                  {attrEditing ? 'Cancel' : 'Edit'}
                </S.SidebarEditLink>
              )
            }>
            {attrEditing ? (
              <S.AttrEdit>
                {isExistingCustomer ? (
                  <>
                    <S.AttrEditField>
                      <Typography variant='body4' color='#808080'>
                        Account owner
                      </Typography>
                      <Select
                        size='small'
                        variant='std-filled'
                        items={team.accountOwners}
                        value={attrDraft.accountOwner}
                        onChange={v => setAttrDraft(d => ({ ...d, accountOwner: v }))}
                        placeholder='Select KAM / EAM'
                        searchProps={{}}
                      />
                    </S.AttrEditField>
                    <S.AttrEditField>
                      <Typography variant='body4' color='#808080'>
                        Technical owner
                      </Typography>
                      <Select
                        size='small'
                        variant='std-filled'
                        items={team.technicalOwners}
                        value={attrDraft.technicalOwner}
                        onChange={v => setAttrDraft(d => ({ ...d, technicalOwner: v }))}
                        onClear={() => setAttrDraft(d => ({ ...d, technicalOwner: '' }))}
                        allowClear
                        allowClearIcon
                        placeholder='Select Technical CSM (optional)'
                        searchProps={{}}
                      />
                    </S.AttrEditField>
                  </>
                ) : (
                  <>
                    <S.AttrEditField>
                      <Typography variant='body4' color='#808080'>
                        AE
                      </Typography>
                      <Select
                        size='small'
                        variant='std-filled'
                        items={aeSelectItems}
                        value={attrDraft.ae}
                        onChange={v => setAttrDraft(d => ({ ...d, ae: v }))}
                        searchProps={{}}
                      />
                      {record.account_executive?.id && !aeOnRoster && (
                        <Typography variant='body5' color='#9E9E9E' sx={{ marginTop: '4px' }}>
                          No longer on the AE roster
                        </Typography>
                      )}
                    </S.AttrEditField>
                    <S.AttrEditField>
                      <Typography variant='body4' color='#808080'>
                        FDE
                      </Typography>
                      <Select
                        size='small'
                        variant='std-filled'
                        items={fdeSelectItems}
                        value={attrDraft.fde}
                        onChange={v => setAttrDraft(d => ({ ...d, fde: v }))}
                        searchProps={{}}
                      />
                      {record.fde?.id && !fdeOnRoster && (
                        <Typography variant='body5' color='#9E9E9E' sx={{ marginTop: '4px' }}>
                          No longer on the FDE roster
                        </Typography>
                      )}
                    </S.AttrEditField>
                    {showPilotOwnerFields && (
                      <>
                        <S.AttrEditField>
                          <Typography variant='body4' color='#808080'>
                            Key Account Manager
                          </Typography>
                          <Select
                            size='small'
                            variant='std-filled'
                            items={kamEditItems}
                            value={attrDraft.accountOwner}
                            onChange={v => setAttrDraft(d => ({ ...d, accountOwner: v }))}
                            onClear={() => setAttrDraft(d => ({ ...d, accountOwner: '' }))}
                            allowClear
                            allowClearIcon
                            placeholder='Select KAM'
                            searchProps={{}}
                          />
                          {record.kam?.id && !kamOnRoster && (
                            <Typography variant='body5' color='#9E9E9E' sx={{ marginTop: '4px' }}>
                              No longer on the KAM roster
                            </Typography>
                          )}
                        </S.AttrEditField>
                        <S.AttrEditField>
                          <Typography variant='body4' color='#808080'>
                            Technical CSM
                          </Typography>
                          <Select
                            size='small'
                            variant='std-filled'
                            items={team.technicalOwners}
                            value={attrDraft.technicalOwner}
                            onChange={v => setAttrDraft(d => ({ ...d, technicalOwner: v }))}
                            onClear={() => setAttrDraft(d => ({ ...d, technicalOwner: '' }))}
                            allowClear
                            allowClearIcon
                            placeholder='Select Technical CSM (optional)'
                            searchProps={{}}
                          />
                        </S.AttrEditField>
                      </>
                    )}
                  </>
                )}
                <S.AttrEditActions>
                  <Button
                    variant='contained'
                    color='primary'
                    size='medium'
                    loading={attrSaving}
                    disabled={pilotClosedLost || (isExistingCustomer && !attrDraft.accountOwner)}
                    onClick={onSaveAttribution}>
                    Save changes
                  </Button>
                  <Button variant='text' color='primary' size='medium' onClick={() => setAttrEditing(false)}>
                    Cancel
                  </Button>
                </S.AttrEditActions>
              </S.AttrEdit>
            ) : (
              <div>
                {attribution.map(a => (
                  <S.AttrReadRow key={a.label}>
                    <Typography variant='body3' color='#808080'>
                      {a.label}
                    </Typography>
                    <Typography variant='body3' sx={{ fontWeight: 500, textAlign: 'right' }}>
                      {a.value}
                    </Typography>
                  </S.AttrReadRow>
                ))}
                {!isExistingCustomer && record.account_executive?.id && !aeOnRoster && (
                  <Typography variant='body5' color='#9E9E9E' sx={{ marginTop: '6px' }}>
                    {record.account_executive.name} — no longer on the AE roster
                  </Typography>
                )}
              </div>
            )}
          </SidebarSection>

          <SidebarSection title='Change history'>
            <S.ChangeLogList>
              {changeLog.map((c, i) => (
                <S.ChangeLogItem key={i}>
                  <Typography variant='body4' color='#4D4D4D' sx={{ lineHeight: 1.45 }}>
                    {c.what}
                  </Typography>
                  <Typography variant='body5' color='#9E9E9E'>
                    {c.who}
                  </Typography>
                </S.ChangeLogItem>
              ))}
            </S.ChangeLogList>
          </SidebarSection>
        </S.Sidebar>

        <S.Main>
          {!hasScope && (
            <WaitingPanel
              email={record.customer_email}
              anchorAt={aging?.agingAnchor || record.created_at}
              agingText={aging?.sinceSent || ''}
              agoText={aging?.elapsed?.text || ''}
              expired={linkExpired}
              neverSent={neverSent}
              stale={!!aging?.stale}
            />
          )}

          {hasScope && (
            <>
              <S.ScopeToolbar>
                <div>
                  <S.ScopeTab>Scope</S.ScopeTab>
                </div>
                <S.ViewToggleWrap>
                  <Typography variant='body4' color='#808080' sx={{ whiteSpace: 'nowrap' }}>
                    {trades.length} trade{trades.length === 1 ? '' : 's'}
                  </Typography>
                  <S.ViewToggleGroup>
                    {['Compact', 'Full'].map(label => {
                      const value = label === 'Compact' ? 'compact' : 'full';
                      return (
                        <S.ViewToggle key={label} on={viewMode === value} onClick={() => setViewMode(value)}>
                          {label}
                        </S.ViewToggle>
                      );
                    })}
                  </S.ViewToggleGroup>
                </S.ViewToggleWrap>
              </S.ScopeToolbar>

              {trades.map((t, i) => {
                const turnaround = resolveRecordTradeTurnaround(t, record);
                const custom = turnaround.custom || null;
                return (
                  <TradeCard
                    key={t.trade_id}
                    trade={t}
                    compact={viewMode === 'compact'}
                    open={i === 0 ? openTrades[t.trade_id] !== false : !!openTrades[t.trade_id]}
                    onToggle={() =>
                      setOpenTrades(prev => {
                        if (i === 0) return { ...prev, [t.trade_id]: prev[t.trade_id] === false ? true : false };
                        return { ...prev, [t.trade_id]: !prev[t.trade_id] };
                      })
                    }
                    turnaround={turnaround}
                    custom={custom}
                    canAct={canActCustom}
                    iso={tatIso}
                    onSetCustom={() => openCustomPanel(t, 'set')}
                    onEditCustom={() => openCustomPanel(t, 'edit')}
                    onRemoveCustom={() =>
                      setCcRemove({
                        trade: t,
                        returnCardName:
                          t.account_preset?.tat?.name ||
                          turnaround.custom?.inPlaceOf ||
                          turnaround.resolved?.card?.name ||
                          '—'
                      })
                    }
                  />
                );
              })}
            </>
          )}
        </S.Main>
      </S.BodyGrid>

      <ReopenModal
        open={reopenOpen}
        onClose={() => {
          if (reopening) return;
          setReopenOpen(false);
        }}
        onConfirm={onReopen}
        submitting={reopening}
        emailsEnabled={emailsEnabled}
        customerEmail={record.customer_email}
      />
      <AdoptModal
        open={adoptOpen}
        onClose={() => {
          if (adopting) return;
          setAdoptOpen(false);
        }}
        onConfirm={onAdopt}
        submitting={adopting}
        companyName={record.company_name}
      />
      <CustomCardModal
        open={!!ccPanel}
        mode={ccPanel?.mode || 'set'}
        tradeName={ccPanel?.tradeName || ''}
        resolvedName={ccPanel?.resolvedName || ''}
        ranges={ccPanel?.ranges || []}
        initialValues={ccPanel?.values || []}
        wasValues={ccPanel?.was || []}
        initialReason={ccPanel?.reason || ''}
        actorName={actorName}
        submitting={ccSaving}
        onCancel={() => {
          if (ccSaving) return;
          setCcPanel(null);
        }}
        onSave={onSaveCustomCard}
      />
      <RemoveCustomCardModal
        open={!!ccRemove}
        tradeName={ccRemove?.trade?.trade_name || ''}
        returnCardName={ccRemove?.returnCardName || '—'}
        actorName={actorName}
        submitting={ccSaving}
        onCancel={() => {
          if (ccSaving) return;
          setCcRemove(null);
        }}
        onConfirm={onConfirmRemoveCustom}
      />
    </S.Wrap>
  );
};

export default DetailView;
